import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useEmployerMyPartner, useEmployerEmployees, useEmployerContributions, QUERY_KEYS } from "@/hooks/use-queries";
import type { EmployerEmployee, EmployerContribution } from "@shared/schema";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  Settings,
  UserPlus,
  Banknote,
  Clock,
  Percent,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PartnerData {
  id: string;
  companyName: string;
  companyCode: string;
  status: string;
  tier: string;
  matchPercent: number;
  matchCapCents: number;
  annualBudgetCents: number;
  annualBudgetUsedCents: number;
  contributionFrequency: string;
  totalContributedCents: number;
  totalEmployeesEnrolled: number;
}

interface EmployeeData {
  id: string;
  employeeName: string;
  employeeEmail: string;
  department: string;
  status: string;
  totalReceivedCents: number;
}

interface ContributionData {
  id: string;
  type: string;
  amountCents: number;
  description?: string;
  employeeName?: string;
  employeeId?: string;
  createdAt: string;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const addEmployeeSchema = z.object({
  employeeName: z.string().min(2, "Name must be at least 2 characters"),
  employeeEmail: z.string().email("Please enter a valid email"),
  department: z.string().min(1, "Department is required"),
});

const fundEmployeeSchema = z.object({
  employeeId: z.string().min(1, "Please select an employee"),
  amountCents: z.coerce.number().min(1, "Amount must be at least $0.01"),
  type: z.enum(["one_time", "recurring", "match"]),
  description: z.string().optional(),
});

const bulkFundSchema = z.object({
  amountCents: z.coerce.number().min(1, "Amount must be at least $0.01"),
});

const perEmployeeFundSchema = z.object({
  amountCents: z.coerce.number().min(0.01, "Amount must be at least $0.01"),
});

const settingsSchema = z.object({
  matchPercent: z.coerce.number().min(0).max(100),
  matchCapCents: z.coerce.number().min(0),
  annualBudgetCents: z.coerce.number().min(0),
  contributionFrequency: z.string().min(1),
});

type AddEmployeeValues = z.infer<typeof addEmployeeSchema>;
type FundEmployeeValues = z.infer<typeof fundEmployeeSchema>;
type BulkFundValues = z.infer<typeof bulkFundSchema>;
type PerEmployeeFundValues = z.infer<typeof perEmployeeFundSchema>;
type SettingsValues = z.infer<typeof settingsSchema>;

function getStatusBadgeVariant(status: string): "default" | "secondary" {
  return status === "enrolled" ? "default" : "secondary";
}

function getContributionTypeBadgeVariant(type: string): "default" | "secondary" | "outline" {
  switch (type) {
    case "recurring":
      return "secondary";
    case "match":
      return "outline";
    default:
      return "default";
  }
}

export default function EmployerDashboardPage() {
  usePageTitle("Employer Benefits Dashboard", "Manage employee health wallet contributions, enrollment, and benefits administration for your organization.");
  const { toast } = useToast();
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [isFundEmployeeOpen, setIsFundEmployeeOpen] = useState(false);
  const [isBulkFundOpen, setIsBulkFundOpen] = useState(false);
  const [fundingEmployee, setFundingEmployee] = useState<EmployeeData | null>(null);

  const { data: partner, isLoading: partnerLoading } = useEmployerMyPartner();

  const { data: employees = [], isLoading: employeesLoading } = useEmployerEmployees();

  const { data: contributions = [], isLoading: contributionsLoading } = useEmployerContributions();

  const addEmployeeForm = useForm<AddEmployeeValues>({
    mode: 'onBlur',
    resolver: zodResolver(addEmployeeSchema),
    defaultValues: {
      employeeName: "",
      employeeEmail: "",
      department: "",
    },
  });

  const fundEmployeeForm = useForm<FundEmployeeValues>({
    mode: 'onBlur',
    resolver: zodResolver(fundEmployeeSchema),
    defaultValues: {
      employeeId: "",
      amountCents: 0,
      type: "one_time",
      description: "",
    },
  });

  const bulkFundForm = useForm<BulkFundValues>({
    mode: 'onBlur',
    resolver: zodResolver(bulkFundSchema),
    defaultValues: {
      amountCents: 0,
    },
  });

  const perEmployeeFundForm = useForm<PerEmployeeFundValues>({
    mode: 'onBlur',
    resolver: zodResolver(perEmployeeFundSchema),
    defaultValues: {
      amountCents: 0,
    },
  });

  const settingsForm = useForm<SettingsValues>({
    mode: 'onBlur',
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      matchPercent: partner?.matchPercent ?? 0,
      matchCapCents: partner?.matchCapCents ? partner.matchCapCents / 100 : 0,
      annualBudgetCents: partner?.annualBudgetCents ? partner.annualBudgetCents / 100 : 0,
      contributionFrequency: partner?.contributionFrequency ?? "monthly",
    },
    values: partner ? {
      matchPercent: partner.matchPercent ?? 0,
      matchCapCents: partner.matchCapCents ? partner.matchCapCents / 100 : 0,
      annualBudgetCents: partner.annualBudgetCents ? partner.annualBudgetCents / 100 : 0,
      contributionFrequency: partner.contributionFrequency ?? "monthly",
    } : undefined,
  });

  const addEmployeeMutation = useMutation({
    mutationFn: async (data: AddEmployeeValues) => {
      const response = await apiRequest("POST", "/api/employer/employees", data);
      return response.json() as Promise<EmployerEmployee>;
    },
    onSuccess: (newEmployee: EmployerEmployee) => {
      queryClient.setQueryData<EmployerEmployee[]>(
        QUERY_KEYS.employerEmployees,
        (prev) => [...(prev ?? []), newEmployee],
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerMyPartner });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerStats });
      setIsAddEmployeeOpen(false);
      addEmployeeForm.reset();
      toast({
        title: "Employee Added",
        description: "The employee has been added to your roster.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add employee",
        variant: "destructive",
      });
    },
  });

  const fundEmployeeMutation = useMutation({
    mutationFn: async (data: FundEmployeeValues) => {
      const response = await apiRequest("POST", "/api/employer/contributions", {
        employeeId: data.employeeId,
        amountCents: Math.round(data.amountCents * 100),
        type: data.type,
        description: data.description,
      });
      return response.json() as Promise<EmployerContribution>;
    },
    onSuccess: (newContribution: EmployerContribution) => {
      queryClient.setQueryData<EmployerContribution[]>(
        QUERY_KEYS.employerContributions,
        (prev) => [newContribution, ...(prev ?? [])],
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerEmployees });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerMyPartner });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerStats });
      setIsFundEmployeeOpen(false);
      fundEmployeeForm.reset();
      toast({
        title: "Contribution Sent",
        description: "The contribution has been processed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send contribution",
        variant: "destructive",
      });
    },
  });

  const bulkFundMutation = useMutation({
    mutationFn: async (data: BulkFundValues) => {
      const response = await apiRequest("POST", "/api/employer/contributions/bulk", {
        amountCents: Math.round(data.amountCents * 100),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerContributions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerEmployees });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerMyPartner });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerStats });
      setIsBulkFundOpen(false);
      bulkFundForm.reset();
      toast({
        title: "Bulk Funding Complete",
        description: "All employees have been funded.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to process bulk funding",
        variant: "destructive",
      });
    },
  });

  const perEmployeeFundMutation = useMutation({
    mutationFn: async (data: PerEmployeeFundValues) => {
      const response = await apiRequest("POST", "/api/employer/contributions", {
        employeeId: fundingEmployee?.id,
        amountCents: Math.round(data.amountCents * 100),
        type: "one_time",
        description: `Contribution for ${fundingEmployee?.employeeName}`,
      });
      return response.json() as Promise<EmployerContribution>;
    },
    onSuccess: (newContribution: EmployerContribution) => {
      queryClient.setQueryData<EmployerContribution[]>(
        QUERY_KEYS.employerContributions,
        (prev) => [newContribution, ...(prev ?? [])],
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerEmployees });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerMyPartner });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerStats });
      setFundingEmployee(null);
      perEmployeeFundForm.reset();
      toast({
        title: "Contribution Sent",
        description: `Funds sent to ${fundingEmployee?.employeeName}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send contribution",
        variant: "destructive",
      });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: SettingsValues) => {
      const response = await apiRequest("PATCH", `/api/employer-partners/${partner?.id}`, {
        matchPercent: data.matchPercent,
        matchCapCents: Math.round(data.matchCapCents * 100),
        annualBudgetCents: Math.round(data.annualBudgetCents * 100),
        contributionFrequency: data.contributionFrequency,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerMyPartner });
      toast({
        title: "Settings Updated",
        description: "Your employer settings have been saved.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  const recentContributions = contributions.slice(0, 5);
  const budgetPercent = partner && partner.annualBudgetCents > 0
    ? Math.round((partner.annualBudgetUsedCents / partner.annualBudgetCents) * 100)
    : 0;

  if (partnerLoading) {
    return (
      <div className="space-y-6 p-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-8 w-52" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1">
          {[80, 88, 112, 72].map((w, i) => (
            <Skeleton key={i} className="h-9 rounded-md" style={{ width: w }} />
          ))}
        </div>

        {/* Company info card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
            <div className="flex items-center gap-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3.5 w-28" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </CardHeader>
        </Card>

        {/* 4 metric cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 56, value: 28, sub: 40 },
            { label: 64, value: 20, sub: 36 },
            { label: 72, value: 24, sub: 52 },
            { label: 48, value: 20, sub: 44 },
          ].map((w, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <Skeleton className="h-4 rounded" style={{ width: w.label }} />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 rounded" style={{ width: w.value * 3 }} />
                <Skeleton className="h-3 rounded" style={{ width: w.sub }} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent contributions card */}
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-44" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between gap-4 p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Employer Dashboard</h1>
          <p className="text-muted-foreground">
            Manage your company's health benefit program
          </p>
        </div>
      </div>

      <div className="p-3 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1" data-testid="text-employer-not-insurance">
        <p>Employer health wallet contributions are facilitated through HealthWallet technology. This is not an employer-sponsored health benefit plan under ERISA or similar regulations. HealthWallet provides technology services only. All funds are processed and held by Stripe, Inc. Employers are responsible for their own tax and regulatory compliance.</p>
        <p>See our <a href="/terms" className="underline inline-flex items-center min-h-[44px]" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline inline-flex items-center min-h-[44px]" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" className="min-h-[44px]" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="employees" className="min-h-[44px]" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="contributions" className="min-h-[44px]" data-testid="tab-contributions">Contributions</TabsTrigger>
          <TabsTrigger value="settings" className="min-h-[44px]" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {partner && (
            <Card className="hover-elevate">
              <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
                <div className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <CardTitle data-testid="text-company-name">{partner.companyName}</CardTitle>
                    <p className="text-sm text-muted-foreground" data-testid="text-company-code">
                      Code: {partner.companyCode}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={partner.status === "active" ? "default" : "secondary"} data-testid="badge-status">
                    {partner.status}
                  </Badge>
                  <Badge variant="outline" data-testid="badge-tier">
                    {partner.tier}
                  </Badge>
                </div>
              </CardHeader>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Contributed</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-total-contributed">
                  {formatCents(partner?.totalContributedCents ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground">Lifetime contributions</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Employees Enrolled</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-employee-count">
                  {partner?.totalEmployeesEnrolled ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">Active employees</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Annual Budget Used</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-budget-used">
                  {budgetPercent}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatCents(partner?.annualBudgetUsedCents ?? 0)} of {formatCents(partner?.annualBudgetCents ?? 0)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Match Rate</CardTitle>
                <Percent className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-match-rate">
                  {partner?.matchPercent ?? 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Up to {formatCents(partner?.matchCapCents ?? 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent Contributions</CardTitle>
            </CardHeader>
            <CardContent>
              {contributionsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between gap-4 p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-5 w-14 rounded-full" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentContributions.length > 0 ? (
                <div className="space-y-3">
                  {recentContributions.map((contribution) => (
                    <div
                      key={contribution.id}
                      className="flex items-center justify-between gap-4 p-3 rounded-lg border"
                      data-testid={`contribution-row-${contribution.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">
                            {contribution.employeeName || "All Employees"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(contribution.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={getContributionTypeBadgeVariant(contribution.type)}>
                          {contribution.type}
                        </Badge>
                        <span className="font-semibold text-sm" data-testid={`text-amount-${contribution.id}`}>
                          {formatCents(contribution.amountCents)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Banknote className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No contributions yet</p>
                  <p className="text-sm text-muted-foreground">Fund your employees to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold">Employee Roster</h2>
            <Dialog open={isAddEmployeeOpen} onOpenChange={setIsAddEmployeeOpen}>
              <DialogTrigger asChild>
                <Button className="min-h-[44px]" data-testid="button-add-employee">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Employee</DialogTitle>
                  <DialogDescription>
                    Add a new employee to your health benefit program.
                  </DialogDescription>
                </DialogHeader>
                <Form {...addEmployeeForm}>
                  <form onSubmit={addEmployeeForm.handleSubmit((data) => addEmployeeMutation.mutate(data))} className="space-y-4">
                    <FormField
                      control={addEmployeeForm.control}
                      name="employeeName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employee Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Full name" {...field} data-testid="input-employee-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={addEmployeeForm.control}
                      name="employeeEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="employee@company.com" {...field} data-testid="input-employee-email" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={addEmployeeForm.control}
                      name="department"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Department</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Engineering" {...field} data-testid="input-department" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={addEmployeeMutation.isPending}
                      data-testid="button-submit-employee"
                    >
                      {addEmployeeMutation.isPending ? "Adding..." : "Add Employee"}
                    </Button>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              {employeesLoading ? (
                <div className="space-y-2">
                  {/* Column header row */}
                  <div className="grid grid-cols-5 gap-4 px-3 py-2">
                    {[48, 52, 60, 40, 56].map((w, i) => (
                      <Skeleton key={i} className="h-3.5 rounded" style={{ width: w }} />
                    ))}
                  </div>
                  <Separator />
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="grid grid-cols-5 gap-4 items-center px-3 py-3 rounded-lg border">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-4 w-14 ml-auto" />
                    </div>
                  ))}
                </div>
              ) : employees.length > 0 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-4 px-3 py-2 text-sm font-medium text-muted-foreground">
                    <span>Name</span>
                    <span>Email</span>
                    <span>Department</span>
                    <span>Status</span>
                    <span className="text-right">Actions</span>
                  </div>
                  <Separator />
                  {employees.map((employee) => (
                    <div
                      key={employee.id}
                      className="grid grid-cols-5 gap-4 items-center px-3 py-3 rounded-lg border"
                      data-testid={`employee-row-${employee.id}`}
                    >
                      <span className="font-medium text-sm" data-testid={`text-employee-name-${employee.id}`}>
                        {employee.employeeName}
                      </span>
                      <span className="text-sm text-muted-foreground" data-testid={`text-employee-email-${employee.id}`}>
                        {employee.employeeEmail}
                      </span>
                      <span className="text-sm text-muted-foreground" data-testid={`text-employee-dept-${employee.id}`}>
                        {employee.department}
                      </span>
                      <div>
                        <Badge variant={getStatusBadgeVariant(employee.status)} data-testid={`badge-employee-status-${employee.id}`}>
                          {employee.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-sm font-medium" data-testid={`text-employee-received-${employee.id}`}>
                          {formatCents(employee.totalReceivedCents)}
                        </span>
                        <Button
                          variant="outline"
                          className="min-h-[44px]"
                          onClick={() => {
                            setFundingEmployee(employee);
                            perEmployeeFundForm.reset({ amountCents: 0 });
                          }}
                          data-testid={`button-fund-employee-${employee.id}`}
                        >
                          Fund
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No employees yet</p>
                  <p className="text-sm text-muted-foreground">Add employees to start managing benefits</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contributions" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold">Contributions</h2>
            <div className="flex gap-2 flex-wrap">
              <Dialog open={isFundEmployeeOpen} onOpenChange={setIsFundEmployeeOpen}>
                <DialogTrigger asChild>
                  <Button className="min-h-[44px]" data-testid="button-fund-employee">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Fund Employee
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Fund Employee</DialogTitle>
                    <DialogDescription>
                      Send a contribution to an individual employee.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...fundEmployeeForm}>
                    <form onSubmit={fundEmployeeForm.handleSubmit((data) => fundEmployeeMutation.mutate(data))} className="space-y-4">
                      <FormField
                        control={fundEmployeeForm.control}
                        name="employeeId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Employee</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger data-testid="select-employee">
                                  <SelectValue placeholder="Select an employee" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {employees.map((emp) => (
                                  <SelectItem key={emp.id} value={String(emp.id)}>
                                    {emp.employeeName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={fundEmployeeForm.control}
                        name="amountCents"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                {...field}
                                data-testid="input-fund-amount"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={fundEmployeeForm.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Type</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger data-testid="select-contribution-type">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="one_time">One Time</SelectItem>
                                <SelectItem value="recurring">Recurring</SelectItem>
                                <SelectItem value="match">Match</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={fundEmployeeForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Add a note" {...field} data-testid="input-fund-description" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={fundEmployeeMutation.isPending}
                        data-testid="button-submit-fund"
                      >
                        {fundEmployeeMutation.isPending ? "Processing..." : "Send Contribution"}
                      </Button>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>

              <Dialog open={isBulkFundOpen} onOpenChange={setIsBulkFundOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="min-h-[44px]" data-testid="button-bulk-fund">
                    <Users className="h-4 w-4 mr-2" />
                    Bulk Fund All
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Bulk Fund All Employees</DialogTitle>
                    <DialogDescription>
                      Send the same contribution amount to every enrolled employee.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...bulkFundForm}>
                    <form onSubmit={bulkFundForm.handleSubmit((data) => bulkFundMutation.mutate(data))} className="space-y-4">
                      <FormField
                        control={bulkFundForm.control}
                        name="amountCents"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount per Employee ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                {...field}
                                data-testid="input-bulk-amount"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={bulkFundMutation.isPending}
                        data-testid="button-submit-bulk"
                      >
                        {bulkFundMutation.isPending ? "Processing..." : "Fund All Employees"}
                      </Button>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              {contributionsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center justify-between gap-4 p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-36" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap justify-end">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-5 w-14 rounded-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : contributions.length > 0 ? (
                <div className="space-y-2">
                  {contributions.map((contribution) => (
                    <div
                      key={contribution.id}
                      className="flex items-center justify-between gap-4 p-3 rounded-lg border"
                      data-testid={`contribution-item-${contribution.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">
                            {contribution.employeeName || "All Employees"}
                          </p>
                          {contribution.description && (
                            <p className="text-xs text-muted-foreground">{contribution.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(contribution.createdAt), "MMM d, yyyy")}
                        </span>
                        <Badge variant={getContributionTypeBadgeVariant(contribution.type)} data-testid={`badge-type-${contribution.id}`}>
                          {contribution.type}
                        </Badge>
                        <span className="font-semibold text-sm" data-testid={`text-contribution-amount-${contribution.id}`}>
                          {formatCents(contribution.amountCents)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No contributions yet</p>
                  <p className="text-sm text-muted-foreground">Use the buttons above to fund your employees</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-muted-foreground" />
                Benefit Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...settingsForm}>
                <form onSubmit={settingsForm.handleSubmit((data) => updateSettingsMutation.mutate(data))} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={settingsForm.control}
                      name="matchPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Match Percent (%)</FormLabel>
                          <FormControl>
                            <Input type="number" min={0} max={100} {...field} data-testid="input-match-percent" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={settingsForm.control}
                      name="matchCapCents"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Match Cap ($)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" min={0} {...field} data-testid="input-match-cap" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={settingsForm.control}
                      name="annualBudgetCents"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Annual Budget ($)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" min={0} {...field} data-testid="input-annual-budget" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={settingsForm.control}
                      name="contributionFrequency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contribution Frequency</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger data-testid="select-frequency">
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="biweekly">Bi-weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="annually">Annually</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Separator />
                  <Button
                    type="submit"
                    disabled={updateSettingsMutation.isPending}
                    data-testid="button-save-settings"
                  >
                    {updateSettingsMutation.isPending ? "Saving..." : "Save Settings"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={fundingEmployee !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFundingEmployee(null);
            perEmployeeFundForm.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fund Employee</DialogTitle>
            <DialogDescription>
              Send a contribution to {fundingEmployee?.employeeName}.
            </DialogDescription>
          </DialogHeader>
          <Form {...perEmployeeFundForm}>
            <form
              onSubmit={perEmployeeFundForm.handleSubmit((data) =>
                perEmployeeFundMutation.mutate(data)
              )}
              className="space-y-4"
            >
              <FormField
                control={perEmployeeFundForm.control}
                name="amountCents"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount to Fund ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        data-testid="input-per-employee-fund-amount"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={perEmployeeFundMutation.isPending}
                data-testid="button-submit-per-employee-fund"
              >
                {perEmployeeFundMutation.isPending ? "Processing..." : "Send Contribution"}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
